import type { UseCase } from '@cleanpro/common-ui';
import { CustomerResponse } from '../types';
import { CustomerRepository } from '../repositories/CustomerRepository';
import { Customer } from '../entities/Customer';

export interface UpdateCustomerRequest {
  id: string;
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  preferences?: string;
}

export class UpdateCustomerUseCase implements UseCase<UpdateCustomerRequest, CustomerResponse> {
  constructor(private customerRepository: CustomerRepository) {}

  async execute(request: UpdateCustomerRequest): Promise<CustomerResponse> {
    // Find existing customer
    const existingCustomer = await this.customerRepository.findById(request.id);
    if (!existingCustomer) {
      throw new Error('Customer not found');
    }

    // Validate unique email if email is being updated
    if (request.email && request.email !== existingCustomer.email) {
      await this.validateUniqueEmail(request.email);
    }

    // Update customer
    const updatedCustomer = existingCustomer.updateProfile({
      name: request.name,
      email: request.email,
      phone: request.phone,
      address: request.address,
      preferences: request.preferences
    });

    // Save to repository
    const savedCustomer = await this.customerRepository.save(updatedCustomer);

    // Return DTO
    return this.toDTO(savedCustomer);
  }

  private async validateUniqueEmail(email: string): Promise<void> {
    const existingCustomer = await this.customerRepository.findByEmail(email);
    if (existingCustomer) {
      throw new Error('Customer with this email already exists');
    }
  }

  private toDTO(customer: Customer): CustomerResponse {
    return {
      id: customer.id,
      name: customer.name,
      email: customer.email,
      phone: customer.phone,
      address: customer.address,
      preferences: customer.preferences,
      rating: customer.rating,
      totalSpent: customer.totalSpent,
      lastService: customer.lastService,
      createdAt: customer.createdAt.toISOString(),
      updatedAt: customer.updatedAt.toISOString()
    };
  }
}